import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import TextInput from "@scripts/react/components/TextInput";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { useCurrentWorkspace } from "@/store";
import { AnalyticsFlowEvent } from "@/analytics/types";
import Button from "@components/Button";
import { ModalWithIcon } from "@scripts/react/components/RfModal/ModalWithIcon";
import { useModel } from "@/contexts/ModelContext";
import { Chip } from "@scripts/react/components/Chip";
import * as Sentry from "@sentry/react";

interface FeedbackFormProps {}

export const FeedbackForm: React.FC<FeedbackFormProps> = () => {
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedbackType, setFeedbackType] = useState<"thumbs-up" | "thumbs-down" | null>(null);
    const [feedbackText, setFeedbackText] = useState("");
    const [hasEverLabeled, setHasEverLabeled] = useState(false);
    const { track } = useAnalytics();
    const currentWorkspace = useCurrentWorkspace();
    const { user } = useAuth();
    const { promptLabels } = useModel();
    const objectNames = useMemo(() => Array.from(promptLabels.keys()), [promptLabels]);

    // Track if user has ever created a label during this session
    React.useEffect(() => {
        if (objectNames.length > 0 && !hasEverLabeled) {
            setHasEverLabeled(true);
        }
    }, [objectNames.length, hasEverLabeled]);

    const feedbackOptions = useMemo(() => {
        if (!feedbackType) {
            return {
                icon: "fad fa-heart",
                title: "Thanks for trying Rapid!",
                label: "How could we improve your experience?"
            };
        }
        return feedbackType === "thumbs-up"
            ? {
                  icon: "fad fa-thumbs-up",
                  title: "Thank you for your feedback!",
                  label: "Anything else you'd like to add?"
              }
            : {
                  icon: "fad fa-thumbs-down",
                  title: "Thank you for your feedback",
                  label: "Why didn't this work for you?"
              };
    }, [feedbackType]);

    const openFeedback = (choice: "thumbs-up" | "thumbs-down") => {
        setFeedbackType(choice);
        setShowFeedbackModal(true);
        track({
            event: AnalyticsFlowEvent.FEEDBACK_OPENED,
            data: { type: feedbackType, workspaceId: currentWorkspace?.id, user: user?.uid }
        });
    };

    const submitFeedback = (submitMethod: "closed-modal" | "submit-button") => {
        if (!feedbackType) return;

        if (feedbackText.length > 0) {
            track({
                event: AnalyticsFlowEvent.FEEDBACK_SUBMITTED,
                data: {
                    type: feedbackType,
                    submitMethod,
                    feedback: feedbackText,
                    workspaceId: currentWorkspace?.id,
                    user: user?.uid,
                    objectNames,
                    sentrySessionId:
                        Sentry.getClient()?.getIntegrationByName("Replay")?.getReplayId() ?? ""
                }
            });
        }

        setShowFeedbackModal(false);
        setFeedbackText("");
    };

    return (
        <>
            <AnimatePresence>
                {hasEverLabeled && (
                    <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{
                            duration: 0.3,
                            ease: [0.4, 0, 0.2, 1],
                            type: "spring",
                            stiffness: 300,
                            damping: 30
                        }}
                        className="hidden items-center gap-2 md:flex"
                    >
                        <motion.span
                            className="text-sm text-tertiary"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                        >
                            How's your experience?
                        </motion.span>
                        <motion.div
                            className="flex"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1, duration: 0.2 }}
                        >
                            {/* @ts-expect-error - Button component type issue */}
                            <Button
                                type="ghost"
                                size="xxsmall"
                                shape="square"
                                className="!text-sm"
                                aria-label="Thumbs up"
                                onClick={() => openFeedback("thumbs-up")}
                            >
                                <i
                                    className={
                                        feedbackType === "thumbs-up"
                                            ? "fas fa-thumbs-up"
                                            : "far fa-thumbs-up"
                                    }
                                ></i>
                            </Button>
                            {/* @ts-expect-error - Button component type issue */}
                            <Button
                                type="ghost"
                                size="xxsmall"
                                shape="square"
                                className="!text-sm"
                                aria-label="Thumbs down"
                                onClick={() => openFeedback("thumbs-down")}
                            >
                                <i
                                    className={
                                        feedbackType === "thumbs-down"
                                            ? "fas fa-thumbs-down"
                                            : "far fa-thumbs-down"
                                    }
                                ></i>
                            </Button>
                        </motion.div>
                        <motion.div
                            className="flex"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1, duration: 0.2 }}
                        >
                            <Chip
                                onClick={() =>
                                    window.open(
                                        "https://calendly.com/d/ctbb-mtw-k4s/roboflow-rapid-general-onboarding",
                                        "_blank",
                                        "noopener,noreferrer"
                                    )
                                }
                                type="text"
                                color="white"
                                className="cursor-pointer hover:bg-gray-50"
                            >
                                Talk with our Team
                            </Chip>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            {/* @ts-expect-error - ModalWithIcon component type issue */}
            <ModalWithIcon
                isOpen={showFeedbackModal}
                onClose={() => submitFeedback("closed-modal")}
                onSubmit={() => submitFeedback("submit-button")}
                submitButtonText="Submit Feedback"
                icon={feedbackOptions.icon}
                title={feedbackOptions.title}
            >
                {/* @ts-expect-error - TextInput component type issue */}
                <TextInput
                    multiline={3}
                    onChange={setFeedbackText}
                    value={feedbackText}
                    placeholder="Type your feedback here"
                    label={feedbackOptions.label}
                    fullWidth
                />
            </ModalWithIcon>
        </>
    );
};
